import { useState, useEffect } from 'react';
import { useLang } from '@/hooks/useLang';

const BASE = 'https://www.yamamoto-ss.co.jp/sp_recycle';

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#0d0d0d]/95 backdrop-blur-md border-b border-white/10'
            : 'bg-transparent'
        }`}
      >
        {/* Top micro bar */}
        <div className={`transition-all duration-500 overflow-hidden ${scrolled ? 'h-0 opacity-0' : 'h-8 opacity-100'}`}>
          <div className="bg-[#c0392b] text-white text-[10px] flex items-center justify-between px-8 h-8">
            <span className="font-bold tracking-[0.2em] uppercase">
              {t('EPS Recycling Equipment — North America', '環境機器事業部 — 北米向け')}
            </span>
            <div className="flex items-center gap-5">
              <a href="https://www.yamamoto-ss.co.jp/company/" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors hidden md:block">
                {t('Company', '企業情報')}
              </a>
              <a href="https://www.yamamoto-ss.co.jp/parts_login/" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors hidden md:block">
                {t('Dealer Login', '取扱店専用')}
              </a>
            </div>
          </div>
        </div>

        {/* Main nav */}
        <div className="flex items-center justify-between px-8 h-16">
          {/* Logo */}
          <a href={BASE} className="flex items-center gap-3 whitespace-nowrap group">
            <div className="w-1 h-6 bg-[#c0392b] group-hover:h-8 transition-all duration-300"></div>
            <span className="text-lg font-black text-white tracking-[0.15em] uppercase">Yamamoto</span>
            <span className="hidden sm:block text-[10px] text-white/40 border-l border-white/20 pl-3 leading-tight uppercase tracking-widest">
              {t('Environmental Div.', '環境機器事業部')}
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-0 h-full">
            {/* Products dropdown */}
            <div
              className="relative h-16 flex items-center"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <button className="px-5 h-full text-sm font-medium text-white/80 hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
                {t('Products', '製品一覧')}
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${productsOpen ? 'rotate-180' : ''}`}></i>
              </button>
              <div
                className={`absolute top-full left-0 bg-[#0d0d0d]/98 backdrop-blur-md border border-white/10 z-50 flex gap-0 min-w-[700px] transition-all duration-300 ${
                  productsOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
                }`}
              >
                <div className="p-6 min-w-[220px] border-r border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-px bg-[#c0392b]"></div>
                    <a href={`${BASE}/products/melter/`} className="text-xs font-bold text-[#c0392b] uppercase tracking-widest hover:text-white transition-colors">
                      {t('EPS Compactors', '発泡スチロール減容機')}
                    </a>
                  </div>
                  <ul className="space-y-2">
                    {['RE-E201', 'RE-E502/502X', 'RE-E1000', 'RE-E1000A/1500/2000/3000'].map(m => (
                      <li key={m}><a href={`${BASE}/products/melter/`} className="text-xs text-white/50 hover:text-white transition-colors pl-3 block">{m}</a></li>
                    ))}
                  </ul>
                </div>
                <div className="p-6 min-w-[220px] border-r border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-px bg-[#c0392b]"></div>
                    <a href={`${BASE}/products/pet/`} className="text-xs font-bold text-[#c0392b] uppercase tracking-widest hover:text-white transition-colors">
                      {t('PET Recycling', 'ペットボトルリサイクル')}
                    </a>
                  </div>
                  <ul className="space-y-2">
                    {['PLS-2000', 'PLS-5000', 'PBD-5000', 'MBS-3000'].map(m => (
                      <li key={m}><a href={`${BASE}/products/pet/`} className="text-xs text-white/50 hover:text-white transition-colors pl-3 block">{m}</a></li>
                    ))}
                  </ul>
                </div>
                <div className="p-6 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-px bg-[#c0392b]"></div>
                    <span className="text-xs font-bold text-[#c0392b] uppercase tracking-widest">{t('More', 'その他')}</span>
                  </div>
                  <ul className="space-y-2.5">
                    {[
                      { label: t('Bag Breakers', '破袋機'), href: `${BASE}/products/bag/` },
                      { label: t('Vertical Conveyors', '垂直搬送機'), href: `${BASE}/products/transport/` },
                      { label: t('Granule Compactors', '造粒減容機'), href: `${BASE}/products/grush/` },
                      { label: t('"Purasiru" Plastic ID', 'ぷらしる'), href: `${BASE}/products/placil/` },
                    ].map(item => (
                      <li key={item.label}><a href={item.href} className="text-xs text-white/50 hover:text-white transition-colors block">{item.label}</a></li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <a href={`${BASE}/case/`} className="px-5 h-16 flex items-center text-sm font-medium text-white/80 hover:text-white transition-colors whitespace-nowrap">
              {t('Case Studies', '導入事例')}
            </a>
            <a href={`${BASE}/concept/`} className="px-5 h-16 flex items-center text-sm font-medium text-white/80 hover:text-white transition-colors whitespace-nowrap">
              {t('Philosophy', '開発への思い')}
            </a>

            {/* About dropdown */}
            <div
              className="relative h-16 flex items-center"
              onMouseEnter={() => setAboutOpen(true)}
              onMouseLeave={() => setAboutOpen(false)}
            >
              <button className="px-5 h-full text-sm font-medium text-white/80 hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
                {t('About', '私たちについて')}
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${aboutOpen ? 'rotate-180' : ''}`}></i>
              </button>
              <div
                className={`absolute top-full left-0 bg-[#0d0d0d]/98 backdrop-blur-md border border-white/10 z-50 min-w-[320px] transition-all duration-300 ${
                  aboutOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
                }`}
              >
                <div className="p-5 flex flex-col gap-3">
                  {[
                    { label: t('Introduction', 'はじめに'), href: `${BASE}/about/` },
                    { label: t('Mission', '事業ミッション'), href: `${BASE}/about/mission.html` },
                    { label: t('Our History', '環境機器事業部の歩み'), href: `${BASE}/about/history.html` },
                  ].map(item => (
                    <a key={item.label} href={item.href} className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-2">
                      <i className="ri-arrow-right-s-line text-[#c0392b]"></i>
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Lang toggle */}
            <div className="flex items-center border border-white/20 rounded-sm overflow-hidden">
              <button
                onClick={() => setLang('en')}
                className={`px-2.5 py-1 text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap ${lang === 'en' ? 'bg-[#c0392b] text-white' : 'text-white/50 hover:text-white'}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('ja')}
                className={`px-2.5 py-1 text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap ${lang === 'ja' ? 'bg-[#c0392b] text-white' : 'text-white/50 hover:text-white'}`}
              >
                JP
              </button>
            </div>

            <a
              href={`${BASE}/contact/`}
              className="hidden md:flex items-center gap-2 bg-[#c0392b] text-white text-xs font-bold px-5 py-2.5 hover:bg-[#a93226] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              {t('Contact', 'お問い合わせ')}
            </a>

            {/* Hamburger */}
            <button
              className="md:hidden flex flex-col gap-1.5 p-2 cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              <span className={`block w-5 h-0.5 bg-white transition-all ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`block w-5 h-0.5 bg-white transition-all ${menuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-5 h-0.5 bg-white transition-all ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden bg-[#0d0d0d]/98 backdrop-blur-md border-t border-white/10 overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-8 py-5 space-y-4">
            {[
              { label: t('Products', '製品一覧'), href: `${BASE}/products/melter/` },
              { label: t('Case Studies', '導入事例'), href: `${BASE}/case/` },
              { label: t('Philosophy', '開発への思い'), href: `${BASE}/concept/` },
              { label: t('About Us', '私たちについて'), href: `${BASE}/about/` },
              { label: t('Contact', 'お問い合わせ'), href: `${BASE}/contact/` },
            ].map(item => (
              <a key={item.label} href={item.href} className="block text-sm text-white/70 hover:text-white transition-colors py-1 border-b border-white/10">
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </header>
    </>
  );
}
